import { useEffect, useState } from 'react';
import api from '../services/api';
import { msgErro } from '../services/ui';
import { useModalSafeClose } from '../helpers/useModalSafeClose';
import './ModalUltimasVendas.css';

const brl = (n) => 'R$ ' + Number(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtNum = (n, d = 2) => Number(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: d });
// '2026-08-22' -> '22/08/2026' (sem new Date, que desloca por fuso).
const fmtData = (iso) => { const [a, m, d] = String(iso || '').split('-'); return a ? `${d}/${m}/${a}` : '—'; };

// O riscado só aparece quando houve desconto NESTE item — aí sim é negociação do produto.
// "pedido" ou "cupom" — onde o desconto foi dado (venda com pedido × venda direta no caixa)
const doc = (v) => (v.origem === 'caixa' ? 'cupom' : 'pedido');
const explicaDescontoItem = (v) => `Preço no ${doc(v)}: ${brl(v.precoCheio)}. Desconto aplicado neste item: ${brl(v.descItem)}.`;
// O desconto geral da nota é do fechamento da venda (rateado entre todos os produtos),
// não da negociação deste produto — fica como observação, sem mexer no preço.
const explicaRateio = (v) =>
  `O ${doc(v)} teve desconto no total, rateado entre os produtos: ${brl(v.rateioDoc)} coube a este item. `
  + `Descontando, a receita deste item foi ${brl(v.totalLiquido)} — é esse valor que entra em Vendas e no Giro.`;

/**
 * Últimas vendas de um SKU — nota, pedido, loja, vendedor, quantidade e preço
 * praticado. Aberto ao clicar na "última venda" (aba Giro e detalhe do produto).
 * `filiais` restringe às lojas filtradas na tela; `filialUnica` mostra só uma loja.
 */
export default function ModalUltimasVendas({ codpro, nome, unid, filiais = [], filialUnica, onFechar }) {
  const [vendas, setVendas] = useState(null); // null = carregando
  const [erro, setErro] = useState('');
  const safe = useModalSafeClose(onFechar);

  useEffect(() => {
    const fils = filialUnica ? [filialUnica] : filiais;
    api.get(`/vendas/produto/${codpro}/ultimas-vendas`, { params: { filiais: fils.join(','), limite: 15 } })
      .then((r) => setVendas(r.data.vendas))
      .catch((e) => { setErro(msgErro(e, 'Erro ao carregar as vendas.')); setVendas([]); });
  }, [codpro, filialUnica]); // eslint-disable-line

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onFechar?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onFechar]);

  const escopo = filialUnica ? 'nesta loja' : (filiais.length ? `nas ${filiais.length} lojas filtradas` : 'em todas as lojas');
  const un = String(unid || '').toUpperCase() === 'M2' ? 'm²' : (unid || 'un').toLowerCase();

  return (
    <div className="uv-overlay" {...safe.overlayProps}>
      <div className="uv-modal">
        <div className="uv-head">
          <div className="uv-head-txt">
            <div className="uv-titulo">Últimas vendas</div>
            <div className="uv-sub">#{codpro} · {nome} <span className="uv-escopo">· {escopo}</span></div>
          </div>
          <button className="uv-x" onClick={onFechar} aria-label="Fechar"><i className="bi bi-x-lg" /></button>
        </div>

        <div className="uv-body">
          {vendas === null ? (
            <div className="uv-vazio"><div className="spinner-border spinner-border-sm" role="status" /> carregando...</div>
          ) : erro ? (
            <div className="uv-vazio text-danger"><i className="bi bi-exclamation-triangle me-1" />{erro}</div>
          ) : vendas.length === 0 ? (
            <div className="uv-vazio">Nenhuma venda registrada.</div>
          ) : (
            <table className="uv-tab">
              <thead>
                <tr>
                  <th>Data</th>
                  <th title="Loja que vendeu — quando o CD fatura por uma loja, a venda continua sendo da loja">Loja</th>
                  <th className="num">Qtd</th>
                  <th className="num">Preço unit.</th>
                  <th className="num">Total</th>
                  <th>Vendedor</th>
                  <th>Documento</th>
                </tr>
              </thead>
              <tbody>
                {vendas.map((v) => (
                  <tr key={`${v.pedido || v.cupom}-${v.filial}-${v.data}-${v.qtd}`}>
                    <td data-label="Data" className="uv-data">{fmtData(v.data)}</td>
                    <td data-label="Loja">{v.filialNome}</td>
                    <td data-label="Qtd" className="num">{fmtNum(v.qtd)} {un}</td>
                    <td data-label="Preço unit." className="num uv-preco">
                      {brl(v.preco)}
                      {v.descItem > 0 && (
                        <div className="uv-cheio" title={explicaDescontoItem(v)}>
                          de {brl(v.precoCheio)} <i className="bi bi-info-circle" />
                        </div>
                      )}
                    </td>
                    <td data-label="Total" className="num">
                      {brl(v.total)}
                      {v.rateioDoc > 0 && (
                        <div className="uv-rateio" title={explicaRateio(v)}>
                          <i className="bi bi-info-circle" /> −{brl(v.rateioDoc)} desc. do {doc(v)}
                        </div>
                      )}
                    </td>
                    <td data-label="Vendedor">{v.vendedor || '—'}</td>
                    <td data-label="Documento" className="uv-doc">
                      {v.origem === 'caixa' ? <>Cupom {v.cupom}</> : <>Pedido {v.pedido}</>}
                      {v.origem === 'pedido' && (
                        <div className="uv-ped">{v.faturadoEm ? `faturado ${fmtData(v.faturadoEm)}` : 'ainda sem nota'}</div>)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="uv-foot">
          <span><i className="bi bi-info-circle me-1" />Data = do <b>pedido</b> (venda fechada). Preço = o <b>negociado para este produto</b>; desconto dado no total do pedido não entra — é do fechamento da venda e aparece como observação no total.</span>
          <button className="btn btn-sm btn-link" onClick={onFechar}>Fechar</button>
        </div>
      </div>
    </div>
  );
}
